<template>
  <div class="content">
    <form class="form" @submit.prevent>
      <div class="form__group form__group--gap-md form__group--flex-end">
        <t-text
          v-model="filters.application_fulltext"
          label="Поиск по заявлению"
          placeholder="ФИО, уникальный код ССПВО или e-mail"
          @update:model-value="value => onTextFilterInput('application_fulltext', value)"
        />
      </div>

      <div class="form__group form__group--gap-md form__group--flex-end">
        <t-select
          v-model="filters.status"
          label="Статус заявления"
          :options="statusOptions"
          @update:model-value="value => onSelectFilter('status', value)"
        />
        <t-select
          v-model="filters.education_level"
          label="Уровень образования"
          :options="educationLevelOptions"
          @update:model-value="value => onSelectFilter('education_level', value)"
        />
        <t-select
          v-model="filters.has_contract"
          label="Договор"
          :options="contractOptions"
          @update:model-value="value => onSelectFilter('has_contract', value)"
        />
        <button class="btn btn--primary" type="button" @click="resetFilters">Сбросить</button>
      </div>
    </form>

    <div v-if="visibleLoading" class="content content--center">
      <p>Загрузка...</p>
    </div>

    <template v-else>
      <div v-if="!store.applications.length" class="empty-state" role="status">
        <span class="empty-state__icon" aria-hidden="true">
          <img class="icon" src="~images/info.svg" v-svg-inline/>
        </span>
        <div class="empty-state__content">
          <h2 class="empty-state__title">Заявления не найдены</h2>
          <p class="empty-state__text">Попробуйте изменить параметры поиска или сбросить фильтры.</p>
        </div>
      </div>

      <div v-else class="join join--col join--lg">
        <table class="table table__inner table--tablet-vertical table--lorem">
          <colgroup>
            <col span="1" style="width: 12%;">
            <col span="1" style="width: 20%;">
            <col span="1" style="width: 14%;">
            <col span="1" style="width: 14%;">
            <col span="1" style="width: 13%;">
            <col span="1" style="width: 17%;">
            <col span="1" style="width: 10%;">
          </colgroup>
          <thead class="table__thead">
            <tr class="table__tr">
              <th
                v-for="column in tableColumns"
                :key="column.title"
                class="table__th"
                :class="{ 'table__th--sortable': column.sortField }"
                :role="column.sortField ? 'button' : null"
                :tabindex="column.sortField ? 0 : null"
                @click="column.sortField && toggleSort(column.sortField)"
                @keydown.enter.prevent="column.sortField && toggleSort(column.sortField)"
                @keydown.space.prevent="column.sortField && toggleSort(column.sortField)"
              >
                <span v-if="column.sortField" class="table__th-content">
                  <span>{{ column.title }}</span>
                  <span
                    class="icon-arrow-up"
                    :class="sortIconClass(column.sortField)"
                    aria-hidden="true"
                  ></span>
                </span>
                <template v-else>{{ column.title }}</template>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              class="table__tr"
              v-for="application in store.applications"
              :key="application.id"
            >
              <td class="table__td" data-title="Код ССПВО">
                <router-link
                  :to="{
                    name: 'secretary.application',
                    params: { formId: application.form_id, applicationId: application.id, section: 'overview' },
                  }"
                >
                  <div v-if="application.sspvo_unique_code">{{ application.sspvo_unique_code }}</div>
                  <div v-else>Заявление</div>
                </router-link>
              </td>
              <td class="table__td" data-title="ФИО абитуриента">
                <router-link :to="{ name: 'secretary.form', params: { id: application.form_id, tab: 'form' } }">
                  {{ application.full_name }}
                </router-link>
              </td>
              <td class="table__td" data-title="Тип заявления">{{ application.education_level }}</td>
              <td class="table__td" data-title="Статус заявления">
                <span class="join join--col join--sm">
                  <span class="join join--center">
                    {{ application.status_text }}
                    <span
                      v-if="application.email_matched === false"
                      v-tooltip="{ content: APPLICATION_EMAIL_NOT_MATCHED_TEXT, theme: 'tooltip-danger' }"
                    >
                      <img class="card__icon icon icon--danger" src="~images/info.svg" v-svg-inline/>
                    </span>
                  </span>
                  <span
                    class="badge badge--pill badge--wrap"
                    :class="application.has_enrolment_agreement ? 'badge--solid-success' : 'badge--solid-muted'"
                  >
                    {{ application.has_enrolment_agreement ? 'Подано согласие на зачисление' : 'Нет согласия на зачисление' }}
                  </span>
                </span>
              </td>
              <td class="table__td" data-title="Дата обновления заявления">{{ application.updated_at }}</td>
              <td class="table__td" data-title="Документы">
                <div class="join join--col join--sm">
                  <a
                    v-if="application.scans_zip_url"
                    class="btn btn--outline-primary btn--xs"
                    :href="application.scans_zip_url"
                  >
                    Скачать документы
                  </a>
                  <button
                    v-else
                    class="btn btn--outline-primary btn--xs"
                    type="button"
                    disabled
                  >
                    Скачать документы
                  </button>

                  <a
                    v-if="application.documents_zip_url"
                    class="btn btn--outline-primary btn--xs"
                    :href="application.documents_zip_url"
                  >
                    Скачать договор
                  </a>
                  <button
                    v-else
                    class="btn btn--outline-primary btn--xs"
                    type="button"
                    disabled
                  >
                    Скачать договор
                  </button>
                </div>
              </td>
              <td class="table__td" data-title="Дата обновления документов">
                {{ application.scans_updated_at }}
              </td>
            </tr>

          </tbody>
        </table>
      </div>

      <t-paginate
        v-if="store.pagination.pages > 1"
        :pages="store.pagination.pages"
        :current="store.pagination.page"
        @change-page="page => updateQuery({ page })"
      />
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch, watchEffect, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { TText, TSelect, TPaginate } from '@shared/ui'
import { APPLICATION_EMAIL_NOT_MATCHED_TEXT, KINDS } from '@shared/config'
import { useDebounce } from '@shared/lib'
import { useSecretaryStore } from '@pages/secretary/model/store'

const APPLICATION_STATUSES = [
  { value: 'accepted', text: 'Принято' },
  { value: 'participating', text: 'Участвует в конкурсе' },
  { value: 'enrolled', text: 'Зачислен' },
  { value: 'dismissed', text: 'Отчислен' },
  { value: 'not_passed', text: 'Не прошел по конкурсу' },
  { value: 'withdrawn', text: 'Отозвано' },
]

const store = useSecretaryStore()
const route = useRoute()
const router = useRouter()
const visibleLoading = ref(false)

const SORT_DIRECTIONS = {
  Ascending: 'asc',
  Descending: 'desc',
}

const tableColumns = [
  { title: 'Код ССПВО', sortField: 'sspvo_unique_code' },
  { title: 'ФИО абитуриента', sortField: 'full_name' },
  { title: 'Тип заявления', sortField: 'education_level' },
  { title: 'Статус заявления', sortField: 'status' },
  { title: 'Дата обновления заявления', sortField: 'updated_at' },
  { title: 'Документы' },
  { title: 'Дата обновления документов', sortField: 'scans_updated_at' },
]

const initialFilters = () => ({
  form_fullname: route.query.form_fullname || '',
  application_fulltext: route.query.application_fulltext || '',
  status: route.query.status || null,
  education_level: route.query.education_level || null,
  has_contract: route.query.has_contract || null,
})

const filters = ref(initialFilters())

const withCount = (text, count) => Number.isInteger(count) ? `${text} (${count})` : text

const statusOptions = computed(() =>
  APPLICATION_STATUSES.map(status => ({
    ...status,
    text: withCount(status.text, store.statusCounts[status.value]),
  }))
)

const educationLevelOptions = computed(() =>
  KINDS.map(kind => ({
    ...kind,
    text: withCount(kind.text, store.educationLevelCounts[kind.value]),
  }))
)

const contractOptions = computed(() => [
  { value: 'true', text: withCount('Есть договор', store.contractCounts.with_contract) },
  { value: 'false', text: withCount('Нет договора', store.contractCounts.without_contract) },
])

const showLoadingDebounced = useDebounce(() => {
  visibleLoading.value = true
}, 180)


const searchParams = computed(() => {
  const { form_fullname, application_fulltext, status, education_level, has_contract, order_by, order, page } = route.query
  const params = {}
  const application = {}

  if (form_fullname) params.form = { fulltext: form_fullname }
  if (application_fulltext) application.fulltext = application_fulltext
  if (status) application.status = status
  if (education_level) application.education_level = education_level
  if (has_contract) application.has_contract = has_contract
  if (order_by) application.order_by = order_by
  if (order) application.order = order
  if (Object.keys(application).length) params.application = application
  if (page) params.page = Number(page)

  return params
})

const updateQuery = (params) => {
  const query = { ...route.query, ...params }
  Object.keys(query).forEach((key) => {
    if (query[key] === null || query[key] === undefined || query[key] === '') delete query[key]
  })
  router.replace({ query })
}

const onTextFilterInput = useDebounce((key, value) => updateQuery({ [key]: value, page: null }))
const onSelectFilter = (key, value) => updateQuery({ [key]: value, page: null })

const toggleSort = (field) => {
  const currentField = route.query.order_by
  const currentDirection = route.query.order

  if (currentField === field && currentDirection === SORT_DIRECTIONS.Ascending) {
    updateQuery({ order_by: field, order: SORT_DIRECTIONS.Descending, page: null })
  } else if (currentField === field && currentDirection === SORT_DIRECTIONS.Descending) {
    updateQuery({ order_by: null, order: null, page: null })
  } else {
    updateQuery({ order_by: field, order: SORT_DIRECTIONS.Ascending, page: null })
  }
}

const sortIconClass = (field) => ({
  'icon-arrow-up--active': route.query.order_by === field,
  'icon-arrow-up--reverted': route.query.order_by === field && route.query.order === SORT_DIRECTIONS.Descending,
})
watch(
  () => store.loading,
  (loading) => {
    if (!loading) {
      visibleLoading.value = false
      showLoadingDebounced.cancel?.()
      return
    }

    showLoadingDebounced()
  },
  { immediate: true }
)

const resetFilters = () => {
  filters.value = {
    form_fullname: '',
    application_fulltext: '',
    status: null,
    education_level: null,
    has_contract: null,
  }
  router.replace({ query: {} })
}

watchEffect(() => store.fetchForms(searchParams.value))
</script>
